import { ActionIcon, Badge, Box, Button, Card, Grid, Group, NumberInput, Select,Text, Textarea, TextInput} from "@mantine/core";
import { IconDeviceFloppy, IconPencil, IconTrash, IconX } from "@tabler/icons-react";
import { useForm } from "@mantine/form";

export default function KpiCardEditor({
  kpi,
  kpiCategoryOptions = [],
  kpiTypeOptions = [],
  criterionOptions = [],
  isEditing,
  onEdit,
  onCancel,
  onSave,
  onDelete,
}) {

  const form = useForm({
    initialValues: {
      id: kpi.id ?? null,
      cid: kpi.cid ?? null,
      number: kpi.number ?? "",
      name: kpi.name ?? "",
      description: kpi.description ?? "",
      category: kpi.category ?? null,
      type: kpi.type ?? null,
      helpText: kpi.helpText ?? "",
      example: kpi.example ?? "",
      criterionId: kpi.criterionId ?? null,
    },
    validate: {
      number: (v) => {
        const n = parseInt(v, 10);
        return Number.isInteger(n) && n >= 1 ? null : "Debe ser un entero ≥ 1";
      },
      name: (v) => (v?.trim() ? null : "Requerido"),
      description: (v) => (v?.trim() ? null : "Requerido"),
      criterionId: (v) => (v ? null : "Requerido"),
      category: (v) => (v ? null : "Requerido"),
      type: (v) => (v ? null : "Requerido"),
    },
  });

  const submit = (vals) => {
    const n = parseInt(vals.number, 10);
    onSave({ ...kpi, ...vals, number: n });
  };

  const view = (
    <Card withBorder radius="md" p="md">
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Group wrap="nowrap">
          <Text size="lg" fw={700} c="#e72166">{kpi.number}.</Text>
          <Box>
            <Text fw={600}>{kpi.name || "Sin nombre"}</Text>
            <Text size="sm" c="dimmed" mt={2}>
              {kpi.description || "Sin descripción"}
            </Text>
            <Group gap="xs" mt={6}>
              <Badge variant="default">
                {criterionOptions.find(c => c.value === kpi.criterionId)?.label || "—"}
              </Badge>
              <Badge>
                {kpiTypeOptions.find(t => t.value === kpi.type)?.label || kpi.type}
              </Badge>
              <Badge>
                {kpiCategoryOptions.find(c => c.value === kpi.category)?.label || kpi.category}
              </Badge>
            </Group>
            
            {(kpi.helpText || kpi.example) && (
              <Grid gutter="xs" mt={6}>
                {kpi.helpText && (
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <Text size="xs"><Text span fw={600}>Ayuda:</Text> {kpi.helpText}</Text>
                  </Grid.Col>
                )}
                {kpi.example && (
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <Text size="xs"><Text span fw={600}>Ejemplo:</Text> {kpi.example}</Text>
                  </Grid.Col>
                )}
              </Grid>
            )}
          </Box>
        </Group>

        <Group gap="xs" wrap="nowrap">
          <ActionIcon variant="light" onClick={() => onEdit(kpi.cid ?? kpi.id)} aria-label="Editar">
            <IconPencil size={16} />
          </ActionIcon>
          <ActionIcon color="red" variant="light" onClick={() => onDelete(kpi.cid ?? kpi.id)} aria-label="Eliminar">
            <IconTrash size={16} />
          </ActionIcon>
        </Group>
      </Group>
    </Card>
  );

  const edit = (
    <Card withBorder radius="md" p="md">
      <form onSubmit={form.onSubmit(submit)}>
        <Grid gutter="sm">
          <Grid.Col span={{ base: 12, md: 3 }}>
            <NumberInput
              label="Número"
              placeholder="1, 2, 3..."
              min={1}
              allowNegative={false}
              allowDecimal={false}
              value={form.values.number}
              onChange={(val) => form.setFieldValue('number', val ?? '')}
              error={form.errors.number}
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 9 }}>
            <TextInput
              label="Nombre"
              placeholder="Ej.: Tiempo de respuesta promedio"
              {...form.getInputProps('name')}
            />
          </Grid.Col>

          <Grid.Col span={12}>
            <Textarea
              label="Descripción"
              placeholder="Describí el KPI"
              autosize
              minRows={2}
              {...form.getInputProps('description')}
            />
          </Grid.Col>

          <Grid.Col span={12}>
            <Select
              label="Criterio"
              placeholder="Vincular a criterio…"
              data={criterionOptions}
              value={form.values.criterionId}
              onChange={(v) => form.setFieldValue('criterionId', v)}
              searchable
              clearable
              error={form.errors.criterionId}
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 6 }}>
            <Select
              label="Categoría"
              placeholder="Seleccionar categoría…"
              data={kpiCategoryOptions}
              value={form.values.category}
              onChange={(v) => form.setFieldValue('category', v)}
              searchable
              clearable
              error={form.errors.category}
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 6 }}>
            <Select
              label="Tipo"
              placeholder="Seleccionar tipo…"
              data={kpiTypeOptions}
              value={form.values.type}
              onChange={(v) => form.setFieldValue('type', v)}
              searchable
              clearable
              error={form.errors.type}
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 6 }}>
            <TextInput
              label="Ayuda"
              placeholder="Texto de ayuda / instrucciones"
              {...form.getInputProps('helpText')}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <TextInput
              label="Ejemplo"
              placeholder="Un ejemplo concreto"
              {...form.getInputProps('example')}
            />
          </Grid.Col>

          <Grid.Col span={12}>
            <Group justify="flex-end">
              <Button variant="default" leftSection={<IconX size={16} />} onClick={onCancel}>
                Cancelar
              </Button>
              <Button type="submit" leftSection={<IconDeviceFloppy size={16} />}>
                Guardar
              </Button>
            </Group>
          </Grid.Col>
        </Grid>
      </form>
    </Card>
  );

  return isEditing ? edit : view;
}